import { http } from '@/lib/http';
import type { ListQuery, Page } from '@/types/common';
import type {
  AttributeTemplate,
  BundleComponentRow,
  Category,
  CategoryKind,
  DimensionUnit,
  FifoPickSuggestion,
  InventoryItem,
  ItemBatch,
  ItemBatchStatus,
  ItemCatalogStatus,
  ItemColorVariation,
  ItemIdentifier,
  ItemIdentifierKind,
  ItemImage,
  ItemVideo,
  ItemBanner,
  BannerType,
  BannerTypeInput,
  PackagingBox,
  PackagingBoxInput,
  FulfilmentDefaults,
  FulfilmentDefaultsInput,
  ItemType,
  ItemKind,
  ItemUnit,
  SkuStyle,
  StockLevel,
  StockMovement,
  StockMovementType,
  TaxPreference,
  VariantSpec,
  VariantTemplateEntry,
  Warehouse,
  WeightUnit,
  Collection,
  CollectionDetail,
  CollectionStatus,
} from '@/types/inventory';

const BASE = '/api/v1/inventory';

export interface HsnRateAuditChange {
  id: string;
  sku: string;
  name: string;
  hsnCode: string;
  matchedCode: string;
  description: string;
  intraStateTaxRate: number | null;
  interStateTaxRate: number | null;
  suggestedRate: number;
}

export interface HsnRateAuditNoHsn {
  id: string;
  sku: string;
  name: string;
  intraStateTaxRate: number | null;
  interStateTaxRate: number | null;
}

export interface HsnRateAuditNoMatch extends HsnRateAuditNoHsn {
  hsnCode: string;
}

export interface HsnRateAudit {
  summary: {
    total: number;
    inSync: number;
    wouldChange: number;
    noHsn: number;
    noMatch: number;
  };
  wouldChange: HsnRateAuditChange[];
  noHsn: HsnRateAuditNoHsn[];
  noMatch: HsnRateAuditNoMatch[];
}

export interface HsnReapplyResult {
  updated: number;
  skipped: Array<{ id: string; sku: string; reason: string }>;
}

// ── Catalog items ─────────────────────────────────────────────────────

export interface ItemListQuery extends ListQuery {
  /** Catalog status: ACTIVE / DISCONTINUED / ARCHIVED (BE enum). */
  status?: ItemCatalogStatus;
  categoryId?: string;
  search?: string;
  /** Filter by procurement classification (raw material vs finished good). */
  kind?: ItemKind;
  /**
   * Variant scoping (BE default = ROOTS, hides child variants from the
   * top-level catalog list). Pass VARIANTS to browse only children, or ALL
   * to ignore the filter.
   */
  parent?: 'ALL' | 'ROOTS' | 'VARIANTS';
  /**
   * P0 variant-stock fix — when true, list returns only items that can
   * physically hold stock: standalone items (no variants) and every
   * variant row. Parent items with variants are filtered out (they're
   * just templates — stock lives on the children). Use from every
   * stock/order/PO picker.
   */
  pickable?: boolean;
}

export interface ItemCreatePayload {
  sku: string;
  name: string;
  description?: string;
  unitOfMeasure?: string;
  hsnCode?: string;
  categoryId?: string;
  reorderPoint?: number;
  status?: ItemCatalogStatus;

  // Commercial
  itemType?: ItemType;
  /** Raw material vs finished good (defaults FINISHED_GOOD server-side). */
  kind?: ItemKind;
  trackInventory?: boolean;
  brand?: string;
  manufacturer?: string;
  /** J3 — operator-curated merchandising flags. */
  isFeatured?: boolean;
  isNewArrival?: boolean;
  /** K1 — opt-in variants mode. BE refuses toggle-off when children exist. */
  hasVariants?: boolean;
  /**
   * L1 — per-item SKU pattern. Bracketed-segment grammar parsed FE-side
   * at variant generation. Null/empty = default `{parent}-{slug}`.
   */
  skuPattern?: string | null;
  styleCode?: string | null;
  attributeCodes?: Record<string, Record<string, string>> | null;
  /** Item-level attribute values picked from the category's attributeTemplate.
   *  Per attribute: `string` (single-pick) or `string[]` (multi). */
  attributes?: Record<string, string | string[]> | null;
  /** Parent-only SKU style block — gender/fabric/category/colour segments. */
  skuStyle?: SkuStyle | null;
  /** M-color — operator's explicit colour decision; gates the variant template wizard. */
  colorVariation?: ItemColorVariation | null;
  /** Bucket C — opt in to per-batch tracking with mfg/expiry dates. */
  tracksBatches?: boolean;
  /** Slice 12 — composite/bundle SKU. Forces trackInventory off. */
  isBundle?: boolean;

  // Tax
  taxPreference?: TaxPreference;
  intraStateTaxRate?: number;
  interStateTaxRate?: number;

  // Pricing
  sellingPrice?: number;
  mrp?: number;
  salesDescription?: string;
  costPrice?: number;
  purchaseDescription?: string;

  // Fulfilment
  dimensionLength?: number;
  dimensionWidth?: number;
  dimensionHeight?: number;
  dimensionUnit?: DimensionUnit;
  weight?: number;
  weightUnit?: WeightUnit;
  // Packing
  packagedWeight?: number;
  unitsPerCarton?: number;
  fragile?: boolean;
  hazmat?: boolean;

  // Fabric & care (apparel)
  fabricComposition?: import('@/types/inventory').FabricComponent[];
  washCareInstructions?: string[];
  careNotes?: string;
}

/**
 * L2 — `sku` is included so the parent SKU can be renamed; the BE
 * cascades to every child variant in a single transaction. Confirm
 * dialog on the FE warns about the rename before the patch is sent.
 */
export type ItemUpdatePayload = Partial<ItemCreatePayload>;

// ── Stock levels (joined view) ────────────────────────────────────────

export interface StockListQuery {
  itemId?: string;
  warehouseId?: string;
  sku?: string;
  /**
   * Phase C — when passed, returns stock rows for ALL variants of this
   * parent (instead of the parent's own empty stock_levels). Used by
   * the InventoryDetailSheet's Stock tab to drive a variant × warehouse
   * pivot when viewing a parent-with-variants.
   */
  parentItemId?: string;
  page?: number;
  pageSize?: number;
}

/** BE returns `data: { rows: [...] }` for stock levels (NOT items+page). */
interface StockListResponse {
  rows: StockLevel[];
  page?: number;
  pageSize?: number;
  total?: number;
}

// ── Stock movements ───────────────────────────────────────────────────

export interface MovementsListQuery {
  itemId?: string;
  warehouseId?: string;
  movementType?: StockMovementType;
  referenceType?: string;
  referenceId?: string;
  /** Phase C — see StockListQuery.parentItemId. */
  parentItemId?: string;
  page?: number;
  pageSize?: number;
}

interface MovementsListResponse {
  rows: StockMovement[];
  page?: number;
  pageSize?: number;
  total?: number;
}

export type StockMovementInput =
  | 'INBOUND'
  | 'OUTBOUND'
  | 'ADJUSTMENT_INCREMENT'
  | 'ADJUSTMENT_DECREMENT';

export interface RecordMovementPayload {
  itemId: string;
  warehouseId: string;
  movementType: StockMovementInput;
  quantity: number;
  reason?: string;
  referenceType?: string;
  referenceId?: string;
}

export interface InwardLine {
  itemId: string;
  warehouseId: string;
  quantity: number;
  reason?: string;
  /** Slice 16 — when set, BE bumps this PO line's receivedQuantity. */
  purchaseOrderItemId?: string;
}

export interface RecordInwardPayload {
  /** 1–500 lines per call. */
  lines: InwardLine[];
  referenceType?: string;
  referenceId?: string;
  reason?: string;
  /** Slice 16 — when set, BE auto-resolves PO line ids by itemId for this PO. */
  purchaseOrderId?: string;
}

export const inventoryService = {
  // Items (catalog)
  list: (query: ItemListQuery = {}) =>
    http.get<Page<InventoryItem>>(`${BASE}/items`, { params: query }),
  get: (id: string) => http.get<InventoryItem>(`${BASE}/items/${id}`),
  create: (payload: ItemCreatePayload) =>
    http.post<InventoryItem, ItemCreatePayload>(`${BASE}/items`, payload),
  update: (id: string, payload: ItemUpdatePayload) =>
    http.patch<InventoryItem, ItemUpdatePayload>(`${BASE}/items/${id}`, payload),
  /**
   * Look up the suggested GST rate for an HSN code against the platform
   * HSN master. Returns null when no match. Drives the item form's
   * auto-fill of the tax rate.
   */
  lookupHsnRate: (hsn: string) =>
    http.get<{
      hsnCode: string;
      matchedCode: string;
      description: string;
      gstRate: number;
    } | null>(`${BASE}/hsn-rates/${encodeURIComponent(hsn)}`),
  /**
   * Audit the taxable catalog against the HSN→GST master. Drives the
   * "Sync HSN rates" preview — buckets items as in-sync / would-change /
   * no-HSN / HSN-not-in-master.
   */
  hsnRateAudit: () => http.get<HsnRateAudit>(`${BASE}/items/hsn-rate-audit`),
  /** Re-apply current HSN-master GST rates to the given items. */
  reapplyHsnRates: (itemIds: string[]) =>
    http.post<HsnReapplyResult, { itemIds: string[] }>(
      `${BASE}/items/reapply-hsn-rates`,
      { itemIds },
    ),
  /**
   * Delete an item. The BE hard-deletes (freeing the SKU for re-use)
   * when the item has no order / dispatch / PO history, otherwise
   * archives it. The result says which path ran.
   */
  remove: (id: string) =>
    http.delete<{ deleted: boolean; archived: boolean; reason: string }>(
      `${BASE}/items/${id}`,
    ),

  // Test-data tooling — gated by the `test-data-tools` feature flag.
  setTestDataFlag: (id: string, isTestData: boolean) =>
    http.patch<{ id: string; isTestData: boolean }, { isTestData: boolean }>(
      `${BASE}/test-data/items/${id}`,
      { isTestData },
    ),
  previewTestData: () =>
    http.get<import('@/types/inventory').TestDataPreview>(
      `${BASE}/test-data/preview`,
    ),
  purgeTestData: () =>
    http.post<import('@/types/inventory').TestDataPurgeResult, undefined>(
      `${BASE}/test-data/purge`,
      undefined,
    ),

  // Stock (joined view of stock_levels)
  listStock: (query: StockListQuery = {}) =>
    http.get<StockListResponse>(`${BASE}/stock`, { params: query }),

  // Movements (audit ledger)
  listMovements: (query: MovementsListQuery = {}) =>
    http.get<MovementsListResponse>(`${BASE}/movements`, { params: query }),

  // Mutate stock
  recordMovement: (payload: RecordMovementPayload) =>
    http.post<{ ok: true }, RecordMovementPayload>(`${BASE}/movements`, payload),
  /** Bulk receipt: 1+ lines, each (item, warehouse, qty). Used for supplier deliveries. */
  recordInward: (payload: RecordInwardPayload) =>
    http.post<{ ok: true; lineCount: number }, RecordInwardPayload>(`${BASE}/inward`, payload),

  /**
   * Atomic transfer: writes paired TRANSFER_OUT + TRANSFER_IN movements
   * for the same item across two warehouses. Returns both ledger rows.
   */
  transferStock: (payload: {
    itemId: string;
    fromWarehouseId: string;
    toWarehouseId: string;
    /** In BASE units. */
    quantity: number;
    reason?: string;
    referenceType?: string;
    referenceId?: string;
  }) =>
    http.post<{ out: StockMovement; in: StockMovement }, typeof payload>(
      `${BASE}/transfers`,
      payload,
    ),

  // ── Identifiers ───────────────────────────────────────────────────
  listIdentifiers: (itemId: string) =>
    http.get<ItemIdentifier[]>(`${BASE}/items/${itemId}/identifiers`),
  createIdentifier: (
    itemId: string,
    payload: { kind: ItemIdentifierKind; value: string; isPrimary?: boolean },
  ) =>
    http.post<ItemIdentifier, typeof payload>(
      `${BASE}/items/${itemId}/identifiers`,
      payload,
    ),
  updateIdentifier: (
    itemId: string,
    identifierId: string,
    payload: Partial<{ kind: ItemIdentifierKind; value: string; isPrimary: boolean }>,
  ) =>
    http.patch<ItemIdentifier, typeof payload>(
      `${BASE}/items/${itemId}/identifiers/${identifierId}`,
      payload,
    ),
  deleteIdentifier: (itemId: string, identifierId: string) =>
    http.delete<void>(`${BASE}/items/${itemId}/identifiers/${identifierId}`),

  // ── Variants ─────────────────────────────────────────────────────
  listVariants: (parentId: string) =>
    http.get<InventoryItem[]>(`${BASE}/items/${parentId}/variants`),
  saveVariantTemplate: (parentId: string, template: VariantTemplateEntry[]) =>
    http.patch<InventoryItem, { template: VariantTemplateEntry[] }>(
      `${BASE}/items/${parentId}/variants/template`,
      { template },
    ),
  createVariants: (parentId: string, variants: VariantSpec[]) =>
    http.post<InventoryItem[], { variants: VariantSpec[] }>(
      `${BASE}/items/${parentId}/variants`,
      { variants },
    ),
  /**
   * Save the garment size chart — parent measurement template + each
   * size variant's per-point values (inches). Returns parent + variants.
   */
  saveMeasurements: (
    parentId: string,
    payload: {
      template: import('@/types/inventory').MeasurementPoint[];
      variants: Record<string, Record<string, number>>;
    },
  ) =>
    http.put<
      InventoryItem[],
      {
        template: import('@/types/inventory').MeasurementPoint[];
        variants: Record<string, Record<string, number>>;
      }
    >(`${BASE}/items/${parentId}/measurements`, payload),
  /**
   * L3 — soft-remove a child variant (sets status='ARCHIVED' on the
   * BE). The row vanishes from the operator's variants grid but
   * stays referenced by any existing order / invoice line items.
   */
  removeVariant: (parentId: string, variantId: string) =>
    http.delete<void>(`${BASE}/items/${parentId}/variants/${variantId}`),

  // ── Images ───────────────────────────────────────────────────────
  listImages: (itemId: string) =>
    http.get<ItemImage[]>(`${BASE}/items/${itemId}/images`),
  uploadImage: async (itemId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<ItemImage, FormData>(`${BASE}/items/${itemId}/images`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteImage: (itemId: string, imageId: string) =>
    http.delete<void>(`${BASE}/items/${itemId}/images/${imageId}`),
  reorderImages: (itemId: string, ids: string[]) =>
    http.patch<ItemImage[], { ids: string[] }>(
      `${BASE}/items/${itemId}/images/reorder`,
      { ids },
    ),
  /** Tag/untag a gallery image as the storefront packshot (listing photo).
   *  Single per item — tagging one clears any previous packshot. */
  setPackshot: (itemId: string, imageId: string, isPackshot: boolean) =>
    http.patch<ItemImage[], { isPackshot: boolean }>(
      `${BASE}/items/${itemId}/images/${imageId}/packshot`,
      { isPackshot },
    ),

  // ── Hero / banner image (single, replaces on upload) ─────────────
  getHeroImage: (itemId: string) =>
    http.get<ItemImage | null>(`${BASE}/items/${itemId}/hero-image`),
  uploadHeroImage: async (itemId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<ItemImage, FormData>(`${BASE}/items/${itemId}/hero-image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteHeroImage: (itemId: string) =>
    http.delete<void>(`${BASE}/items/${itemId}/hero-image`),

  // ── Storefront video (single per item; upload replaces) ──────────
  getVideo: (itemId: string) =>
    http.get<ItemVideo | null>(`${BASE}/items/${itemId}/video`),
  /**
   * Upload the clip plus the poster still captured from it, and the metrics
   * the browser measured. The backend re-validates all three — it cannot
   * derive duration/resolution itself without a demuxer, so it rejects a
   * request that omits them rather than trusting a default.
   */
  uploadVideo: async (
    itemId: string,
    file: File,
    poster: Blob | null,
    meta: { durationSec: number; width: number; height: number },
    opts?: {
      /** Called as bytes leave the browser. `total` is 0 when unknown. */
      onProgress?: (loaded: number, total: number) => void;
      /** Lets the operator abandon a long upload. */
      signal?: AbortSignal;
    },
  ) => {
    const fd = new FormData();
    fd.append('file', file);
    if (poster) fd.append('poster', poster, 'poster.jpg');
    fd.append('durationSec', String(Math.round(meta.durationSec)));
    fd.append('width', String(meta.width));
    fd.append('height', String(meta.height));
    return http.post<ItemVideo, FormData>(`${BASE}/items/${itemId}/video`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
      signal: opts?.signal,
      onUploadProgress: (e) => opts?.onProgress?.(e.loaded, e.total ?? 0),
      // A 100 MB clip on a slow uplink can legitimately outlast the default
      // client timeout; the operator cancels deliberately instead.
      timeout: 0,
    });
  },
  deleteVideo: (itemId: string) => http.delete<void>(`${BASE}/items/${itemId}/video`),
  /** Record where the storefront serves the clip from once mirrored; null clears it. */
  setVideoPlaybackUrl: (itemId: string, playbackUrl: string | null) =>
    http.patch<ItemVideo, { playbackUrl: string | null }>(
      `${BASE}/items/${itemId}/video/playback-url`,
      { playbackUrl },
    ),

  // ── Portal banners (multiple per placement; drag/move to reorder) ──
  listBanners: (itemId: string) =>
    http.get<ItemBanner[]>(`${BASE}/items/${itemId}/banners`),
  /** Append a new banner image to a banner type. */
  uploadBanner: async (itemId: string, bannerTypeId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<ItemBanner, FormData>(
      `${BASE}/items/${itemId}/banners/type/${bannerTypeId}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  /** Replace one banner's image (keeps placement + position). */
  replaceBanner: async (itemId: string, bannerId: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.put<ItemBanner, FormData>(
      `${BASE}/items/${itemId}/banners/${bannerId}`,
      fd,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
  },
  deleteBanner: (itemId: string, bannerId: string) =>
    http.delete<void>(`${BASE}/items/${itemId}/banners/${bannerId}`),
  /** Reorder a placement — pass the ordered banner ids. */
  reorderBanners: (itemId: string, ids: string[]) =>
    http.patch<ItemBanner[], { ids: string[] }>(
      `${BASE}/items/${itemId}/banners/reorder`,
      { ids },
    ),

  // ── Batches (Bucket C) ───────────────────────────────────────────
  listBatches: (
    itemId: string,
    query: { status?: ItemBatchStatus; includeDepleted?: boolean } = {},
  ) =>
    http.get<ItemBatch[]>(`${BASE}/items/${itemId}/batches`, {
      params: {
        status: query.status,
        includeDepleted:
          query.includeDepleted === undefined ? undefined : query.includeDepleted ? 'true' : 'false',
      },
    }),
  createBatch: (
    itemId: string,
    payload: {
      batchNumber: string;
      mfgDate?: string;
      expiryDate?: string;
      supplierRef?: string;
      notes?: string;
      openingWarehouseId?: string;
      openingQuantity?: number;
    },
  ) => http.post<ItemBatch, typeof payload>(`${BASE}/items/${itemId}/batches`, payload),
  updateBatch: (
    itemId: string,
    batchId: string,
    payload: {
      batchNumber?: string;
      mfgDate?: string | null;
      expiryDate?: string | null;
      supplierRef?: string | null;
      notes?: string | null;
      status?: ItemBatchStatus;
    },
  ) => http.patch<ItemBatch, typeof payload>(`${BASE}/items/${itemId}/batches/${batchId}`, payload),
  deleteBatch: (itemId: string, batchId: string) =>
    http.delete<void>(`${BASE}/items/${itemId}/batches/${batchId}`),
  /** FIFO pick suggestion — returns up to N batch slices summing to `quantity`. */
  suggestFifoPick: (itemId: string, warehouseId: string, quantity: number) =>
    http.get<FifoPickSuggestion[]>(`${BASE}/items/${itemId}/batches/suggest/fifo`, {
      params: { warehouseId, quantity },
    }),

  // ── Multi-UoM (Bucket C, Slice 2) ─────────────────────────────────
  listUnits: (itemId: string) =>
    http.get<ItemUnit[]>(`${BASE}/items/${itemId}/units`),
  createUnit: (
    itemId: string,
    payload: {
      code: string;
      name: string;
      conversionFactor: number;
      isPurchaseDefault?: boolean;
      isSalesDefault?: boolean;
    },
  ) => http.post<ItemUnit, typeof payload>(`${BASE}/items/${itemId}/units`, payload),
  updateUnit: (
    itemId: string,
    unitId: string,
    payload: {
      code?: string;
      name?: string;
      conversionFactor?: number;
      isPurchaseDefault?: boolean;
      isSalesDefault?: boolean;
    },
  ) => http.patch<ItemUnit, typeof payload>(`${BASE}/items/${itemId}/units/${unitId}`, payload),
  deleteUnit: (itemId: string, unitId: string) =>
    http.delete<void>(`${BASE}/items/${itemId}/units/${unitId}`),

  // ── Bundle components (Slice 12) ─────────────────────────────────
  listBundleComponents: (bundleItemId: string) =>
    http.get<BundleComponentRow[]>(`${BASE}/items/${bundleItemId}/bundle-components`),
  addBundleComponent: (
    bundleItemId: string,
    payload: { componentItemId: string; quantity: number; sortOrder?: number },
  ) =>
    http.post<BundleComponentRow, typeof payload>(
      `${BASE}/items/${bundleItemId}/bundle-components`,
      payload,
    ),
  updateBundleComponent: (
    bundleItemId: string,
    componentRowId: string,
    payload: { quantity?: number; sortOrder?: number },
  ) =>
    http.patch<BundleComponentRow, typeof payload>(
      `${BASE}/items/${bundleItemId}/bundle-components/${componentRowId}`,
      payload,
    ),
  removeBundleComponent: (bundleItemId: string, componentRowId: string) =>
    http.delete<void>(
      `${BASE}/items/${bundleItemId}/bundle-components/${componentRowId}`,
    ),
};

// ── Item prices (multi-currency, Slice C1) ────────────────────────────

export interface ItemPrice {
  id: string;
  tenantId: string;
  itemId: string;
  currency: string;
  sellingPrice: number;
  costPrice?: number | null;
  notes?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ItemPriceCreatePayload {
  currency: string;
  sellingPrice: number;
  costPrice?: number;
  notes?: string;
}

export const itemPricesService = {
  list: (itemId: string) => http.get<ItemPrice[]>(`${BASE}/items/${itemId}/prices`),
  create: (itemId: string, payload: ItemPriceCreatePayload) =>
    http.post<ItemPrice, ItemPriceCreatePayload>(`${BASE}/items/${itemId}/prices`, payload),
  update: (
    itemId: string,
    priceId: string,
    payload: Partial<Omit<ItemPriceCreatePayload, 'currency'>>,
  ) =>
    http.patch<ItemPrice, typeof payload>(
      `${BASE}/items/${itemId}/prices/${priceId}`,
      payload,
    ),
  remove: (itemId: string, priceId: string) =>
    http.delete<void>(`${BASE}/items/${itemId}/prices/${priceId}`),
};

// ── Categories ────────────────────────────────────────────────────────

export interface CategoryCreatePayload {
  code: string;
  name: string;
  description?: string;
  parentId?: string;
  /** ADR-040 — storefront visibility (SELLABLE default / INTERNAL ops-only). */
  categoryKind?: CategoryKind;
  /** ADR-040 — typed item-attribute schema for items in this category. */
  attributeTemplate?: AttributeTemplate | null;
}

export const categoriesService = {
  /**
   * BE returns `data: [...]` directly (no pagination wrapper).
   * Inactive (soft-deleted) categories are hidden by default; pass
   * `includeInactive: true` for the catalog management list when the
   * operator wants to restore one.
   */
  list: (opts: { includeInactive?: boolean } = {}) =>
    http.get<Category[]>(`${BASE}/categories`, {
      params: opts.includeInactive ? { includeInactive: 'true' } : undefined,
    }),
  create: (payload: CategoryCreatePayload) =>
    http.post<Category, CategoryCreatePayload>(`${BASE}/categories`, payload),
  update: (id: string, payload: Partial<CategoryCreatePayload>) =>
    http.patch<Category, Partial<CategoryCreatePayload>>(`${BASE}/categories/${id}`, payload),
  /**
   * L3 — soft-delete a category (sets status='INACTIVE'). BE refuses
   * when items still reference it or subcategories are nested under it.
   */
  remove: (id: string) => http.delete<void>(`${BASE}/categories/${id}`),

  // ── Images: square tile + wide banner ──
  uploadImage: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<Category, FormData>(`${BASE}/categories/${id}/image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  clearImage: (id: string) => http.delete<Category>(`${BASE}/categories/${id}/image`),
  uploadBanner: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<Category, FormData>(`${BASE}/categories/${id}/banner`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  clearBanner: (id: string) => http.delete<Category>(`${BASE}/categories/${id}/banner`),
};

// ── Collections (merchandising) ───────────────────────────────────────

export interface CollectionCreatePayload {
  slug: string;
  name: string;
  description?: string;
  status?: CollectionStatus;
  imageUrl?: string;
  bannerUrl?: string;
  sortOrder?: number;
  /** ISO date/datetime strings; null clears the bound. */
  startsAt?: string | null;
  endsAt?: string | null;
}

export const collectionsService = {
  list: (opts: { includeInactive?: boolean } = {}) =>
    http.get<Collection[]>(`${BASE}/collections`, {
      params: opts.includeInactive ? { includeInactive: 'true' } : undefined,
    }),
  get: (id: string) => http.get<CollectionDetail>(`${BASE}/collections/${id}`),
  create: (payload: CollectionCreatePayload) =>
    http.post<Collection, CollectionCreatePayload>(`${BASE}/collections`, payload),
  update: (id: string, payload: Partial<CollectionCreatePayload>) =>
    http.patch<Collection, Partial<CollectionCreatePayload>>(`${BASE}/collections/${id}`, payload),
  remove: (id: string) => http.delete<void>(`${BASE}/collections/${id}`),
  /** Replace-set the collection's member products (array order = display order). */
  setItems: (id: string, itemIds: string[]) =>
    http.put<CollectionDetail, { itemIds: string[] }>(`${BASE}/collections/${id}/items`, {
      itemIds,
    }),
  /** Tile/icon image shown on the collection card on the storefront. */
  uploadImage: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<Collection, FormData>(`${BASE}/collections/${id}/image`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  clearImage: (id: string) => http.delete<Collection>(`${BASE}/collections/${id}/image`),
  /** Wide banner/hero image for the collection landing page. */
  uploadBanner: async (id: string, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return http.post<Collection, FormData>(`${BASE}/collections/${id}/banner`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  clearBanner: (id: string) => http.delete<Collection>(`${BASE}/collections/${id}/banner`),
};

// ── Packaging boxes + fulfilment defaults ─────────────────────────────

export const packagingService = {
  listBoxes: () => http.get<PackagingBox[]>(`${BASE}/packaging-boxes`),
  createBox: (payload: PackagingBoxInput) =>
    http.post<PackagingBox, PackagingBoxInput>(`${BASE}/packaging-boxes`, payload),
  updateBox: (id: string, payload: Partial<PackagingBoxInput>) =>
    http.patch<PackagingBox, Partial<PackagingBoxInput>>(`${BASE}/packaging-boxes/${id}`, payload),
  removeBox: (id: string) => http.delete<void>(`${BASE}/packaging-boxes/${id}`),
  reorderBoxes: (ids: string[]) =>
    http.patch<PackagingBox[], { ids: string[] }>(`${BASE}/packaging-boxes/reorder`, { ids }),
  getDefaults: () => http.get<FulfilmentDefaults>(`${BASE}/fulfilment-defaults`),
  updateDefaults: (payload: FulfilmentDefaultsInput) =>
    http.patch<FulfilmentDefaults, FulfilmentDefaultsInput>(`${BASE}/fulfilment-defaults`, payload),
};

// ── Banner types (tenant-defined placements) ──────────────────────────

export const bannerTypesService = {
  list: () => http.get<BannerType[]>(`${BASE}/banner-types`),
  create: (payload: BannerTypeInput) =>
    http.post<BannerType, BannerTypeInput>(`${BASE}/banner-types`, payload),
  update: (id: string, payload: Partial<BannerTypeInput>) =>
    http.patch<BannerType, Partial<BannerTypeInput>>(`${BASE}/banner-types/${id}`, payload),
  remove: (id: string) => http.delete<void>(`${BASE}/banner-types/${id}`),
  reorder: (ids: string[]) =>
    http.patch<BannerType[], { ids: string[] }>(`${BASE}/banner-types/reorder`, { ids }),
};

// ── Warehouses (tenant view) ──────────────────────────────────────────

export const warehousesService = {
  list: () => http.get<Warehouse[]>(`${BASE}/warehouses`),
};

export type { StockMovementType };
